import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'

import { PersonalSettingsDto, TSP, User } from '@/types.ts'
import { useAppSelector } from '@/stores/hooks.ts'

type UpdatePersonalSettings = {
  settings: Record<string, string[]>
  pageKey: string
}

type TspDictionary = Record<string, TSP>

export interface UserState {
  lastUserId: number | null
  login: string
  user: (User & { tspDictionary: TspDictionary }) | null
  personalSettings: PersonalSettingsDto | null
}

const initialState: UserState = {
  lastUserId: null,
  user: null,
  personalSettings: null,
  login: '',
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  selectors: {
    personalSettings: (state, page) => state.personalSettings?.pages[page],
    user: (state) => state.user,
    login: (state) => state.login,
  },
  reducers: {
    setLogin: (state, action: PayloadAction<string>) => ({ ...state, login: action.payload }),
    setUser: (state, action: PayloadAction<User>) => {
      const user = action.payload
      const tspDictionary: TspDictionary = {}
      user.tspList.forEach((tsp) => (tspDictionary[tsp.merchantId] = tsp))

      return {
        ...state,
        lastUserId: user.id,
        user: {
          ...user,
          tspDictionary,
        },
      }
    },
    setPersonalSettings: (state, action: PayloadAction<PersonalSettingsDto>) => {
      return {
        ...state,
        personalSettings: action.payload,
      }
    },

    //пока нигде не используется, предполгается для оффлайн обновления
    setPersonalSettingsByPageKey: (state, actions: PayloadAction<UpdatePersonalSettings>) => {
      const prevPersonalSettings = cloneDeep(state.personalSettings)
      if (prevPersonalSettings) {
        prevPersonalSettings.pages[actions.payload.pageKey] = actions.payload.settings
        return {
          ...state,
          personalSettings: prevPersonalSettings,
        }
      }

      return {
        ...state,
        personalSettings: {
          pages: {
            [actions.payload.pageKey]: actions.payload.settings,
          },
        },
      }
    },
    resetUser: (state) => {
      return {
        ...state,
        user: null,
      }
    },
    reset: () => initialState,
  },
})

export const { reducer: userReducer, actions: userActions, selectors: userSelectors } = userSlice

export const useUser = () => useAppSelector((state) => state.user.user)
export const useLastUserId = () => useAppSelector((state) => state.user.lastUserId)
export const useLogin = () => useAppSelector((state) => state.user.login)
export const usePersonalSettings = () => useAppSelector((state) => state.user.personalSettings)
